<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="800px" persistent scrollable>
        <v-card>
            <v-card-title class="primary white--text">
                <v-icon left color="white">{{ isEdit ? 'edit' : 'add' }}</v-icon>
                {{ isEdit ? 'Modifica Tipologia' : 'Nuova Tipologia' }}
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="pt-6">
                <v-form ref="form" v-model="valid">
                    <v-row>
                        <!-- Nome -->
                        <v-col cols="12" md="6">
                            <v-text-field v-model="form.nome" label="Nome Tipologia *" outlined dense
                                :rules="[rules.required, rules.nomeUnivoco]" counter="100"
                                maxlength="100"></v-text-field>
                        </v-col>

                        <!-- Categoria -->
                        <v-col cols="12" md="6">
                            <v-combobox v-model="form.categoria" :items="categorieEsistenti" label="Categoria *"
                                outlined dense :rules="[rules.required]" hint="Seleziona o crea una nuova categoria"
                                persistent-hint></v-combobox>
                        </v-col>

                        <!-- Icona Material -->
                        <v-col cols="12" md="6">
                            <v-text-field v-model="form.iconaMaterial" label="Icona Material Design *" outlined dense
                                :rules="[rules.required]" hint="Es: car, laptop, calendar" persistent-hint
                                @input="validateIcon">
                                <template v-slot:prepend-inner>
                                    <v-icon :color="form.coloreHex || 'grey'">
                                        {{ form.iconaMaterial || 'help_outline' }}
                                    </v-icon>
                                </template>
                            </v-text-field>
                        </v-col>

                        <!-- Colore -->
                        <v-col cols="12" md="6">
                            <div class="d-flex align-center">
                                <v-text-field v-model="form.coloreHex" label="Colore (Hex) *" outlined dense
                                    :rules="[rules.required, rules.colorHex]" hint="Es: #2196F3" persistent-hint
                                    class="mr-2" readonly></v-text-field>
                                <input type="color" v-model="form.coloreHex" class="color-picker-input" />
                            </div>
                        </v-col>

                        <!-- Giorni Preavviso Default -->
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.giorniPreavvisoDefault"
                                label="Giorni Preavviso Default *" outlined dense type="number" min="1" max="365"
                                :rules="[rules.required, rules.positiveNumber]"
                                hint="Giorni prima della scadenza per l'avviso" persistent-hint></v-text-field>
                        </v-col>

                        <!-- Ricorrenza Mesi Default -->
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.ricorrenzaMesiDefault" label="Ricorrenza Default (mesi)"
                                outlined dense type="number" min="0" max="120"
                                hint="0 o vuoto = evento singolo, >0 = ricorrente" persistent-hint
                                clearable></v-text-field>
                        </v-col>

                        <!-- Campi Richiesti -->
                        <v-col cols="12">
                            <div class="text-subtitle-2 mb-2">Campi Richiesti</div>
                            <v-checkbox v-model="campiSelezionati" label="Targa" value="targa" dense hide-details
                                class="mt-0"></v-checkbox>
                            <v-checkbox v-model="campiSelezionati" label="Nome PC" value="nomePc" dense hide-details
                                class="mt-0"></v-checkbox>
                            <v-checkbox v-model="campiSelezionati" label="Operatore" value="operatore" dense
                                hide-details class="mt-0"></v-checkbox>
                            <div class="text-caption grey--text mt-2">
                                Seleziona i campi obbligatori per questa tipologia di scadenza
                            </div>
                        </v-col>

                        <!-- Preview -->
                        <v-col cols="12">
                            <v-divider class="mb-4"></v-divider>
                            <div class="text-subtitle-2 mb-2">Anteprima</div>
                            <v-chip :color="form.coloreHex" text-color="white" class="mr-2">
                                <v-icon left>{{ form.iconaMaterial || 'help_outline' }}</v-icon>
                                {{ form.nome || 'Nome Tipologia' }}
                            </v-chip>
                            <v-chip outlined class="mr-2">
                                {{ form.categoria || 'Categoria' }}
                            </v-chip>
                            <v-chip v-if="form.ricorrenzaMesiDefault" outlined small>
                                <v-icon small left>refresh</v-icon>
                                {{ form.ricorrenzaMesiDefault }}
                                {{ form.ricorrenzaMesiDefault === 1 ? 'mese' : 'mesi' }}
                            </v-chip>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-spacer />
                <v-btn text @click="annulla">
                    Annulla
                </v-btn>
                <v-btn color="primary" :loading="saving" :disabled="!valid" @click="salva">
                    <v-icon left>save</v-icon>
                    Salva
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    createTipologia,
    updateTipologia,
    getTipologieScadenze
} from '@/api/api.scadenze.js';

export default {
    name: 'TipologiaFormDialog',

    props: {
        value: {
            type: Boolean,
            default: false
        },
        tipologia: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            valid: false,
            saving: false,

            form: {
                id: null,
                nome: '',
                categoria: '',
                iconaMaterial: '',
                coloreHex: '#2196F3',
                giorniPreavvisoDefault: 7,
                ricorrenzaMesiDefault: null
            },

            campiSelezionati: [],

            categorieEsistenti: [],

            rules: {
                required: v => !!v || 'Campo obbligatorio',
                positiveNumber: v => v > 0 || 'Deve essere maggiore di 0',
                colorHex: v => {
                    if (!v) return 'Campo obbligatorio';
                    return /^#[0-9A-Fa-f]{6}$/.test(v) || 'Formato colore non valido (es: #2196F3)';
                },
                nomeUnivoco: v => {
                    if (!v) return true;
                    // Verifica se il nome esiste già (escluso se stesso in modifica)
                    const exists = this.categorieEsistenti.some(cat =>
                        cat.toLowerCase() === v.toLowerCase() &&
                        (!this.tipologia || this.tipologia.nome.toLowerCase() !== v.toLowerCase())
                    );
                    return !exists || 'Esiste già una tipologia con questo nome';
                }
            }
        };
    },

    computed: {
        isEdit() {
            return !!this.tipologia?.id;
        }
    },

    watch: {
        value(val) {
            if (val) {
                this.inizializzaForm();
                this.caricaCategorieEsistenti();
            } else {
                this.resetForm();
            }
        }
    },

    methods: {
        async caricaCategorieEsistenti() {
            try {
                const response = await getTipologieScadenze();
                const tipologie = response.data.data || response.data || [];

                // Estrai categorie uniche
                this.categorieEsistenti = [...new Set(tipologie.map(t => t.categoria))].sort();
            } catch (error) {
                console.error('Errore nel caricamento delle categorie:', error);
            }
        },

        inizializzaForm() {
            if (this.tipologia) {
                // Modalità modifica o duplica
                this.form = {
                    id: this.tipologia.id,
                    nome: this.tipologia.nome,
                    categoria: this.tipologia.categoria,
                    iconaMaterial: this.tipologia.iconaMaterial,
                    coloreHex: this.tipologia.coloreHex,
                    giorniPreavvisoDefault: this.tipologia.giorniPreavvisoDefault,
                    ricorrenzaMesiDefault: this.tipologia.ricorrenzaMesiDefault
                };

                // Parse campi richiesti
                try {
                    this.campiSelezionati = JSON.parse(this.tipologia.campiRichiesti || '[]');
                } catch {
                    this.campiSelezionati = [];
                }
            } else {
                // Modalità inserimento
                this.resetForm();
            }

            // Reset validazione
            this.$nextTick(() => {
                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }
            });
        },

        resetForm() {
            this.form = {
                id: null,
                nome: '',
                categoria: '',
                iconaMaterial: '',
                coloreHex: '#2196F3',
                giorniPreavvisoDefault: 7,
                ricorrenzaMesiDefault: null
            };
            this.campiSelezionati = [];

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        validateIcon() {
            // Rimuovi spazi e converti in lowercase
            if (this.form.iconaMaterial) {
                this.form.iconaMaterial = this.form.iconaMaterial.trim().toLowerCase();
            }
        },

        async salva() {
            if (!this.$refs.form.validate()) return;

            this.saving = true;

            try {
                const payload = {
                    ...this.form,
                    campiRichiesti: JSON.stringify(this.campiSelezionati),
                    // Se ricorrenzaMesiDefault è 0 o null, imposta a null
                    ricorrenzaMesiDefault: this.form.ricorrenzaMesiDefault || null
                };

                if (this.isEdit) {
                    await updateTipologia(this.form.id, payload);
                } else {
                    delete payload.id;
                    await createTipologia(payload);
                }

                this.$emit('saved', {
                    isEdit: this.isEdit,
                    tipologia: payload
                });
                this.resetForm();
            } catch (error) {
                this.$emit('error', 'Errore nel salvataggio della tipologia');
                console.error(error);
            } finally {
                this.saving = false;
            }
        },

        annulla() {
            this.$emit('close');
            this.resetForm();
        }
    }
};
</script>

<style scoped>
.color-picker-input {
    width: 60px;
    height: 40px;
    border: 2px solid #ccc;
    border-radius: 4px;
    cursor: pointer;
}

.color-picker-input::-webkit-color-swatch-wrapper {
    padding: 0;
}

.color-picker-input::-webkit-color-swatch {
    border: none;
    border-radius: 2px;
}
</style>